import { toCanvas } from 'html-to-image';

// A4 em CSS px (96 dpi) e em pontos PDF.
const A4_WIDTH_PX = 794;
const A4_HEIGHT_PX = 1123;
const PAGE_MARGIN_PX = 53;
const PDF_WIDTH_PT = 595.28;
const PDF_HEIGHT_PT = 841.89;
const BREAK_SELECTORS =
  'tr, .card, .grid, .closing, .next-steps, .header, .intro, .footer, p, table, h1, h2, li';

const encoder = new TextEncoder();

const bytesToBase64 = bytes => {
  let binary = '';
  const chunk = 0x8000;
  for (let index = 0; index < bytes.length; index += chunk) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunk));
  }
  return window.btoa(binary);
};

const dataUrlToBytes = dataUrl => {
  const binary = window.atob(dataUrl.split(',')[1] || '');
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};

const concatBytes = parts => {
  const size = parts.reduce((total, part) => total + part.length, 0);
  const result = new Uint8Array(size);
  let offset = 0;
  parts.forEach(part => {
    result.set(part, offset);
    offset += part.length;
  });
  return result;
};

// PDF mínimo com uma imagem JPEG por página, alinhada ao topo da folha A4.
export const buildImagePdf = pages => {
  const objects = [];
  const pageIds = [];
  const addObject = parts => {
    objects.push(parts);
    return objects.length;
  };
  addObject([]); // catálogo, preenchido ao final
  addObject([]); // árvore de páginas

  pages.forEach(({ jpeg, width, height }) => {
    const drawHeight = Math.min(PDF_HEIGHT_PT, (height / width) * PDF_WIDTH_PT);
    const imageId = addObject([
      encoder.encode(
        `<< /Type /XObject /Subtype /Image /Width ${width} /Height ${height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>\nstream\n`
      ),
      jpeg,
      encoder.encode('\nendstream'),
    ]);
    const content = `q ${PDF_WIDTH_PT} 0 0 ${drawHeight.toFixed(2)} 0 ${(PDF_HEIGHT_PT - drawHeight).toFixed(2)} cm /Im${imageId} Do Q`;
    const contentId = addObject([
      encoder.encode(
        `<< /Length ${content.length} >>\nstream\n${content}\nendstream`
      ),
    ]);
    pageIds.push(
      addObject([
        encoder.encode(
          `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PDF_WIDTH_PT} ${PDF_HEIGHT_PT}] /Resources << /XObject << /Im${imageId} ${imageId} 0 R >> >> /Contents ${contentId} 0 R >>`
        ),
      ])
    );
  });

  objects[0] = [encoder.encode('<< /Type /Catalog /Pages 2 0 R >>')];
  objects[1] = [
    encoder.encode(
      `<< /Type /Pages /Kids [${pageIds.map(id => `${id} 0 R`).join(' ')}] /Count ${pageIds.length} >>`
    ),
  ];

  const chunks = [encoder.encode('%PDF-1.4\n%\xE2\xE3\xCF\xD3\n')];
  const offsets = [];
  let length = chunks[0].length;
  objects.forEach((parts, index) => {
    offsets.push(length);
    const body = concatBytes([
      encoder.encode(`${index + 1} 0 obj\n`),
      ...parts,
      encoder.encode('\nendobj\n'),
    ]);
    chunks.push(body);
    length += body.length;
  });
  const xref = [
    `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`,
    ...offsets.map(offset => `${String(offset).padStart(10, '0')} 00000 n \n`),
    `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${length}\n%%EOF`,
  ].join('');
  chunks.push(encoder.encode(xref));
  return concatBytes(chunks);
};

// Escolhe cortes de página no fim de linhas, cartões e blocos, sem partir conteúdo.
export const pageBreaks = (totalHeight, candidates, pageHeight) => {
  const breaks = [];
  let start = 0;
  const sorted = [...new Set(candidates)].sort((a, b) => a - b);
  while (totalHeight - start > pageHeight) {
    const limit = start + pageHeight;
    const minimum = start + pageHeight * 0.5;
    const fit = sorted.filter(value => value > minimum && value <= limit);
    const cut = fit.length ? fit[fit.length - 1] : limit;
    breaks.push([start, cut]);
    start = cut;
  }
  breaks.push([start, totalHeight]);
  return breaks;
};

const waitForFrame = iframe =>
  new Promise((resolve, reject) => {
    iframe.onload = () => resolve();
    iframe.onerror = () => reject(new Error('quote frame failed'));
  });

const renderDocumentCanvas = async (html, scale) => {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.tabIndex = -1;
  Object.assign(iframe.style, {
    position: 'fixed',
    left: '-10000px',
    top: '0',
    width: `${A4_WIDTH_PX}px`,
    height: `${A4_HEIGHT_PX}px`,
    border: '0',
    visibility: 'hidden',
  });
  const loaded = waitForFrame(iframe);
  iframe.srcdoc = html;
  document.body.appendChild(iframe);
  try {
    await loaded;
    const doc = iframe.contentDocument;
    const style = doc.createElement('style');
    style.textContent = `html, body { width: ${A4_WIDTH_PX}px; background: #ffffff; } body { padding: ${PAGE_MARGIN_PX}px; }`;
    doc.head.appendChild(style);
    await doc.fonts?.ready;
    const body = doc.body;
    const bodyTop = body.getBoundingClientRect().top;
    const candidates = Array.from(doc.querySelectorAll(BREAK_SELECTORS)).map(
      element =>
        Math.round((element.getBoundingClientRect().bottom - bodyTop) * scale)
    );
    const canvas = await toCanvas(body, {
      pixelRatio: scale,
      backgroundColor: '#ffffff',
      width: A4_WIDTH_PX,
      height: Math.ceil(body.scrollHeight),
      cacheBust: false,
    });
    return { canvas, candidates };
  } finally {
    iframe.remove();
  }
};

const sliceCanvas = (canvas, [start, end], pageHeight, topOffset) => {
  const page = document.createElement('canvas');
  page.width = canvas.width;
  page.height = Math.min(pageHeight, end - start + topOffset);
  const context = page.getContext('2d');
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, page.width, page.height);
  context.drawImage(
    canvas,
    0,
    start,
    canvas.width,
    end - start,
    0,
    topOffset,
    canvas.width,
    end - start
  );
  return page;
};

const safeFileBase = name =>
  String(name || 'orcamento')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase()
    .slice(0, 60) || 'orcamento';

/**
 * Gera o orçamento como anexo a partir do mesmo HTML usado em "Gerar PDF".
 * Retorna arquivos em base64 prontos para o envio pela conversa.
 */
export const renderQuoteAttachment = async ({ html, format, name }) => {
  const scale = format === 'png' ? 1.5 : 2;
  const { canvas, candidates } = await renderDocumentCanvas(html, scale);
  const pageHeight = Math.round(canvas.width * (PDF_HEIGHT_PT / PDF_WIDTH_PT));
  // Páginas seguintes recebem a mesma margem superior da primeira.
  const topMargin = Math.round(PAGE_MARGIN_PX * scale);
  const slices = pageBreaks(
    canvas.height,
    candidates,
    pageHeight - topMargin
  ).map((range, index) =>
    sliceCanvas(canvas, range, pageHeight, index === 0 ? 0 : topMargin)
  );
  const base = safeFileBase(name);

  if (format === 'png') {
    return slices.map((page, index) => ({
      filename: slices.length > 1 ? `${base}-${index + 1}.png` : `${base}.png`,
      content_type: 'image/png',
      data: page.toDataURL('image/png').split(',')[1],
    }));
  }

  const pdf = buildImagePdf(
    slices.map(page => ({
      jpeg: dataUrlToBytes(page.toDataURL('image/jpeg', 0.9)),
      width: page.width,
      height: page.height,
    }))
  );
  return [
    {
      filename: `${base}.pdf`,
      content_type: 'application/pdf',
      data: bytesToBase64(pdf),
    },
  ];
};
